import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Movie } from "@/mocks/movies";
import { formatViews } from "@/utils/catalog";

export default function HeroCarousel({ movies }: { movies: Movie[] }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % movies.length);
    }, 6500);
    return () => window.clearInterval(timer);
  }, [movies.length, paused]);

  const active = movies[index];

  return (
    <section
      className="relative w-full overflow-hidden bg-foreground-950"
      aria-label="最新上线作品"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="relative h-[520px] w-full md:h-[600px]">
        {movies.map((movie, slideIndex) => (
          <div
            key={movie.id}
            className={`absolute inset-0 transition-opacity duration-[1200ms] ${
              slideIndex === index ? "opacity-100" : "pointer-events-none opacity-0"
            }`}
            aria-hidden={slideIndex !== index}
          >
            <img
              src={movie.backdrop}
              alt={`${movie.title}电影剧照`}
              title={`${movie.title} - 苍井空电影`}
              className={`h-full w-full object-cover object-center ${slideIndex === index ? "animate-slow-zoom" : ""}`}
            />
          </div>
        ))}

        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/55 to-foreground-950/10" />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-transparent to-foreground-950/30" />

        <div className="relative z-10 flex h-full w-full px-4 md:px-8">
          <div className="mx-auto flex h-full w-full max-w-[1320px] flex-col justify-end pb-14 md:justify-center md:pb-0">
            <div key={active.id} className="max-w-2xl animate-fade-up">
              <p className="flex flex-wrap items-center gap-3 text-[11px] text-background-200">
                <span className="rounded-full bg-primary-500 px-3 py-1 text-[11px] text-background-50">
                  最新上线
                </span>
                <span>{active.year} 年 · {active.region}</span>
                <span className="flex items-center gap-1">
                  <i className="ri-star-fill text-[10px] text-accent-400" />
                  {active.rating.toFixed(1)}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line text-xs" />
                  {formatViews(active.views)}
                </span>
              </p>
              <h2 className="mt-4 font-heading text-3xl leading-tight text-background-50 md:text-[52px]">
                {active.title}
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-background-200 md:mt-4 md:text-base">
                {active.tagline}
              </p>
              <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-background-300 md:text-xs">
                <span>导演 {active.director}</span>
                <span className="h-3 w-px bg-background-500/60" />
                <span>主演 {active.cast.slice(0, 2).join(" / ")}</span>
                <span className="h-3 w-px bg-background-500/60" />
                <span>{active.duration} 分钟</span>
              </div>
              <div className="mt-6 flex flex-col gap-2.5 sm:flex-row sm:items-center">
                <Link
                  to={`/detail/${active.id}`}
                  className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-play-fill text-base" />
                  查看作品详情
                </Link>
                <Link
                  to="/category"
                  className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300/50 bg-background-50/10 px-6 py-3 text-sm text-background-50 backdrop-blur transition-colors hover:bg-background-50/20"
                >
                  浏览全部作品
                  <i className="ri-arrow-right-line text-base" />
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 right-4 z-10 hidden items-center gap-4 md:right-8 lg:flex">
          <div className="flex items-center gap-2">
            {movies.map((movie, dotIndex) => (
              <button
                key={movie.id}
                onClick={() => setIndex(dotIndex)}
                aria-label={`切换到${movie.title}`}
                className={`h-1 rounded-full transition-all ${
                  dotIndex === index ? "w-8 bg-accent-400" : "w-4 bg-background-300/50 hover:bg-background-200"
                }`}
              />
            ))}
          </div>
          <span className="text-xs tabular-nums text-background-300">
            {String(index + 1).padStart(2, "0")} / {String(movies.length).padStart(2, "0")}
          </span>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setIndex((prev) => (prev - 1 + movies.length) % movies.length)}
              aria-label="上一部"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-background-300/40 text-background-100 transition-colors hover:border-accent-400 hover:text-accent-400"
            >
              <i className="ri-arrow-left-s-line text-lg leading-none" />
            </button>
            <button
              onClick={() => setIndex((prev) => (prev + 1) % movies.length)}
              aria-label="下一部"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-background-300/40 text-background-100 transition-colors hover:border-accent-400 hover:text-accent-400"
            >
              <i className="ri-arrow-right-s-line text-lg leading-none" />
            </button>
          </div>
        </div>

        <div className="absolute bottom-5 left-4 z-10 flex items-center gap-2 lg:hidden">
          {movies.map((movie, dotIndex) => (
            <button
              key={movie.id}
              onClick={() => setIndex(dotIndex)}
              aria-label={`切换到${movie.title}`}
              className={`h-1 rounded-full transition-all ${
                dotIndex === index ? "w-7 bg-accent-400" : "w-3.5 bg-background-300/50"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}